import EntityLabel from "../../components/EntityLabel";
import { useState } from "react";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { Order, OrderItem } from "../../models/Order";
import { OrderService } from "../../services/OrderService";
import LineItemEditor, {
  CalculateLineItemTotals,
  ValidateLineItems,
} from "../../components/LineItemEditor/LineItemEditor";
import { useOrder } from "./Order";
import type { DropdownModel } from "@akin2unde/flowui-react";
import "./OrderCss.css";

const money = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
});
const statuses = [
  "Pending",
  "Confirmed",
  "Processing",
  "Ready",
  "Completed",
  "Cancelled",
];
const columns: ManagementColumn<Order>[] = [
  {
    Key: "Order",
    Header: "Order",
    Render: (row) => (
      <IdentityCell
        Icon="fa-cart-shopping"
        Title={row.Code}
        Subtitle={new Date(row.OrderedAt).toLocaleString("en-NG")}
      />
    ),
  },
  { Key: "Customer", Header: "Customer", Render: (row) => <EntityLabel entity="customer" code={row.CustomerCode} /> },
  {
    Key: "Items",
    Header: "Items",
    Render: (row) => `${row.Items?.length ?? 0} items`,
  },
  {
    Key: "Amount",
    Header: "Total",
    Render: (row) => <strong>{money.format(row.TotalAmount)}</strong>,
  },
  {
    Key: "Payment",
    Header: "Payment",
    Render: (row) => <StatusBadge Status={row.PaymentStatus} />,
  },
  {
    Key: "Status",
    Header: "Status",
    Render: (row) => <StatusBadge Status={row.Status} />,
  },
];
const createFields = (
  customers: DropdownModel[],
): ManagementDialogField<Order>[] => [
  { Key: "Code", Label: "Order code", DisableAfterNew: true },
  // { Key: "OrderNumber", Label: "Order number", Required: true },
  {
    Key: "CustomerCode",
    Label: "Customer",
    Type: "select",
    Options: customers,
    Required: true,
  },
  // { Key: "SubTotal", Label: "Subtotal", Type: "money", Required: true },
  { Key: "DiscountAmount", Label: "Discount", Type: "money" },
  // { Key: "TotalAmount", Label: "Total", Type: "money", Required: true },
  { Key: "CurrencyCode", Label: "Currency", Required: true },
  {
    Key: "Status",
    Label: "Order status",
    Type: "select",
    Options: statuses,
    Required: true,
  },
  {
    Key: "PaymentStatus",
    Label: "Payment status",
    Type: "select",
    Options: ["Pending", "PartiallyPaid", "Paid", "Failed", "Refunded"],
    Required: true,
  },
  { Key: "OrderedAt", Label: "Ordered at", Type: "datetime", Required: true },
  { Key: "Notes", Label: "Notes", Type: "textarea", FullWidth: true },
];
export default function OrderHTML() {
  const { Warehouse } = useWarehouse();
  const [unassigned, setUnassigned] = useState(false);
  const [scopeRevision, setScopeRevision] = useState(0);
  const { Customers, Products, Error } = useOrder();
  const fields = createFields(
    Customers.map((customer) => ({
      display: String(customer.Value),
      value: customer.Key,
      other: customer,
    })),
  );
  fields.push({
    Key: "WareHouse",
    Label: "Warehouse (optional)",
    Type: "select",
    Options: [
      { display: "Unassigned", value: "", other: null },
      {
        display: Warehouse?.Name ?? "",
        value: Warehouse?.Code ?? "",
        other: null,
      },
    ],
  });
  return (
    <>
      {Error && <p role="alert">{Error}</p>}
      <div className="warehouse-order-tabs">
        <button
          type="button"
          aria-pressed={!unassigned}
          onClick={() => setUnassigned(false)}
        >
          Current warehouse
        </button>
        <button
          type="button"
          aria-pressed={unassigned}
          onClick={() => setUnassigned(true)}
        >
          Unassigned orders
        </button>
      </div>
      <ManagementPageHTML
        key={`${unassigned ? "unassigned" : "assigned"}:${scopeRevision}`}
        OnSaved={(rows) => {
          if (
            rows.some((row) =>
              unassigned
                ? row.WareHouse != null
                : row.WareHouse !== Warehouse?.Code,
            )
          ) {
            setScopeRevision((value) => value + 1);
          }
        }}
        Service={OrderService(Warehouse?.Code, unassigned)}
        DefaultValues={{
          WareHouse: unassigned ? null : Warehouse?.Code,
          Items: [],
          CurrencyCode: "NGN",
          Status: "Pending",
          PaymentStatus: "Pending",
          OrderedAt: new Date().toISOString(),
        }}
        OnFormValueChange={(field, value, values) => {
          if (field !== "Items" && field !== "DiscountAmount") return;
          const items = (
            field === "Items" ? value : values.Items
          ) as OrderItem[];
          const discount = Number(
            field === "DiscountAmount" ? value : values.DiscountAmount || 0,
          );
          return CalculateLineItemTotals(items ?? [], discount);
        }}
        ValidateForm={(values) => {
          const errors: Record<string, string> = {};
          const error = ValidateLineItems(values.Items, Products);
          if (error) errors.Items = error;
          return errors;
        }}
        RenderDialogContent={({
          FormValues,
          SetFormValue,
          ValidationErrors,
        }) => (
          <LineItemEditor<OrderItem>
            Items={(FormValues.Items as OrderItem[]) ?? []}
            Products={Products}
            DiscountAmount={Number(FormValues.DiscountAmount || 0)}
            CurrencyCode={String(FormValues.CurrencyCode || "NGN")}
            OnChange={(items) => SetFormValue("Items", items)}
            HideSummary
            Error={ValidationErrors.Items}
          />
        )}
        DialogSections={[
          {Title:"Order details", Keys:["Code","CustomerCode","OrderedAt","WareHouse"]},
          {Title:"Payment and summary", Keys:["Status","PaymentStatus","DiscountAmount","CurrencyCode"], Side:true},
          {Title:"Notes", Keys:["Notes"]},
        ]}
        DialogSummary={values => {const totals=CalculateLineItemTotals((values.Items ?? []) as OrderItem[], Number(values.DiscountAmount || 0));return <div className="editor-summary"><div><span>Subtotal</span><strong>{money.format(totals.SubTotal)}</strong></div><div><span>Discount</span><strong>{money.format(Number(values.DiscountAmount || 0))}</strong></div><div className="editor-summary-total"><span>Total</span><strong>{money.format(totals.TotalAmount)}</strong></div></div>;}}
        Title="Orders"
        Eyebrow="Order fulfilment"
        Description="Create orders and track every stage from pending to completion. Status changes are retained by the backend order history."
        AddLabel="New order"
        SearchPlaceholder="Search order or customer"
        Rows={[]}
        Columns={columns}
        DialogFields={fields}
        Statuses={statuses}
        GetRowKey={(row) => row.Code}
        GetSearchText={(row) =>
          `${row.Code} ${row.OrderNumber} ${row.CustomerCode}`
        }
        GetStatus={(row) => row.Status}
        Summaries={[]}
        GetSummaries={(rows, total) => [
          {
            Label: "Orders",
            Value: total,
            Icon: "fa-cart-shopping",
            Tone: "Blue",
          },
          {
            Label: "Order value",
            Value: money.format(
              rows.reduce((sum, x) => sum + x.TotalAmount, 0),
            ),
            Icon: "fa-naira-sign",
            Tone: "Green",
          },
          {
            Label: "Processing",
            Value: rows.filter((x) => x.Status === "Processing").length,
            Icon: "fa-gears",
            Tone: "Purple",
          },
          {
            Label: "Pending",
            Value: rows.filter((x) => x.Status === "Pending").length,
            Icon: "fa-clock",
            Tone: "Orange",
          },
        ]}
        Filters={[
          {
            Key: "PaymentStatus",
            Label: "Payment status",
            Options: [
              "Unpaid",
              "Pending",
              "PartiallyPaid",
              "Paid",
              "Failed",
              "Refunded",
            ],
            GetValue: (row) => row.PaymentStatus,
          },
        ]}
      />
    </>
  );
}
